import * as React from 'react';

export interface SelectOption {
  value: string;
  label: string | React.ReactNode;
}

interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string;
  options: SelectOption[];
  placeholder?: string;
  hideArrow?: boolean;
  renderOption?: (option: SelectOption, isSelected: boolean) => React.ReactNode;
  renderSelectedValue?: (option: SelectOption | null) => React.ReactNode;
  className?: string;
}

export const Select = React.forwardRef<
  HTMLSelectElement,
  SelectProps
>((props, forwardedRef) => {
  const {
    label,
    options,
    placeholder = 'Select an option',
    hideArrow = false,
    renderOption,
    renderSelectedValue,
    disabled,
    className,
    defaultValue,
    value: controlledValue,
    onChange: userOnChange,
    ...otherProps
  } = props;

  // Are we in "controlled" mode?
  const isControlled = controlledValue != null;

  // Internal state for the uncontrolled case
  const [internalValue, setInternalValue] = React.useState<string>(
    () =>
      typeof defaultValue === 'string'
        ? defaultValue
        : ''
  );

  // Which value do we actually display?
  const currentValue = isControlled
    ? String(controlledValue)
    : internalValue;

  const selectedOption =
    options.find((o) => o.value === currentValue) || null;

  // refs
  const containerRef = React.useRef<HTMLDivElement>(null);
  const listboxRef = React.useRef<HTMLUListElement>(null);
  const nativeSelectRef = React.useRef<HTMLSelectElement>(null);

  // expose the real <select> via forwardedRef
  React.useImperativeHandle(
    forwardedRef,
    () => nativeSelectRef.current as HTMLSelectElement
  );

  // When the parent changes `value` in controlled mode, force the native select's .value too
  React.useEffect(() => {
    if (isControlled && nativeSelectRef.current) {
      nativeSelectRef.current.value = String(controlledValue);
    }
  }, [controlledValue, isControlled]);

  const dispatchNativeChange = () => {
    if (!nativeSelectRef.current) return;
    const evt = new Event('change', { bubbles: true });
    nativeSelectRef.current.dispatchEvent(evt);
  };

  // clicking an option
  const handleOptionClick = (val: string) => {
    // set the underlying <select>
    if (nativeSelectRef.current) {
      nativeSelectRef.current.value = val;
      dispatchNativeChange();
    }
    // update internal if uncontrolled
    if (!isControlled) {
      setInternalValue(val);
    }
    setIsOpen(false);
  };

  // dropdown open / close + focus index
  const [isOpen, setIsOpen] = React.useState(false);
  const [focusedIndex, setFocusedIndex] = React.useState(-1);

  const handleToggleDropdown = () => {
    if (disabled) return;
    setIsOpen((o) => !o);
    if (!isOpen) {
      const idx = options.findIndex((o) => o.value === currentValue);
      setFocusedIndex(idx >= 0 ? idx : 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    switch (e.key) {
      case 'Enter':
      case ' ':
        if (!isOpen) {
          handleToggleDropdown();
        } else if (focusedIndex >= 0) {
          handleOptionClick(options[focusedIndex].value);
        }
        e.preventDefault();
        break;
      case 'Escape':
        setIsOpen(false);
        e.preventDefault();
        break;
      case 'ArrowDown':
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setFocusedIndex((i) => Math.min(i + 1, options.length - 1));
        }
        e.preventDefault();
        break;
      case 'ArrowUp':
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setFocusedIndex((i) => Math.max(i - 1, 0));
        }
        e.preventDefault();
        break;
      case 'Home':
        if (isOpen) setFocusedIndex(0);
        e.preventDefault();
        break;
      case 'End':
        if (isOpen) setFocusedIndex(options.length - 1);
        e.preventDefault();
        break;
    }
  };

  // scroll into view
  React.useEffect(() => {
    if (isOpen && focusedIndex >= 0 && listboxRef.current) {
      const el = listboxRef.current.children[focusedIndex] as HTMLElement;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex, isOpen]);

  // click outside to close
  React.useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="block font-bold relative" ref={containerRef}>
      {label && <div className="mb-1">{label}</div>}

      {/* — Hidden native select — */}
      <select
        ref={nativeSelectRef}
        tabIndex={-1}
        disabled={disabled}
        className="sr-only"
        defaultValue={defaultValue}
        {...(isControlled
          ? {
              value: controlledValue,
              onChange: userOnChange,
            }
          : {
              onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
                setInternalValue(e.target.value);
                userOnChange?.(e);
              },
            })}
        {...otherProps}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {typeof opt.label === 'string' ? opt.label : ''}
          </option>
        ))}
      </select>

      {/* — Your custom combobox UI — */}
      <div
        className={`
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${className ?  className : 'placeholder-tertiary font-normal text-14 border border-border rounded-full w-full outline-none pr-10 pl-4 py-2 flex items-center relative cursor-pointer'}
        `}
        onClick={handleToggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={props.tabIndex ? props.tabIndex : (disabled ? -1 : 0)}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-disabled={disabled}
        aria-controls="select-options-listbox"
      >
        <div className="flex-grow truncate">
          {renderSelectedValue
            ? renderSelectedValue(selectedOption)
            : selectedOption
            ? selectedOption.label
            : <span className="text-tertiary">{placeholder}</span>}
        </div>

        {!hideArrow && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg
              width="10"
              height="6"
              viewBox="0 0 10 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }}
            >
              <path
                d="M1 1L5 5L9 1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>

      {/* — Dropdown list — */}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-background-green-light rounded-3xl shadow-lg py-1 overflow-hidden min-w-80">
          <ul
            ref={listboxRef}
            id="select-options-listbox"
            role="listbox"
            aria-activedescendant={
              focusedIndex >= 0
                ? `select-option-${options[focusedIndex].value}`
                : undefined
            }
            className="max-h-60 overflow-auto"
          >
            {options.map((opt, idx) => {
              const isSel = opt.value === currentValue;
              return (
                <li
                  key={opt.value}
                  id={`select-option-${opt.value}`}
                  role="option"
                  aria-selected={isSel}
                  className={`
                    px-4 py-2 cursor-pointer
                    ${isSel ? 'bg-primary bg-opacity-10 text-primary' : ''}
                    ${focusedIndex === idx ? 'bg-primary bg-opacity-10' : ''}
                  `}
                  onClick={() => handleOptionClick(opt.value)}
                  onMouseEnter={() => setFocusedIndex(idx)}
                >
                  {renderOption
                    ? renderOption(opt, isSel)
                    : opt.label}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
});
